<template>
  <div class="sankey-page">
    <div class="page-header">
      <h2 class="page-title">桑基图分析</h2>
      <div class="header-actions">
        <el-button @click="goBack" type="primary" plain>
          <i class="el-icon-back"></i> 返回雷达图
        </el-button>
        <el-button @click="refreshChart" type="success" plain>
          <i class="el-icon-refresh"></i> 刷新图表
        </el-button>
      </div>
    </div>

    <div class="control-panel">
      <div class="panel-section">
        <h3>数据配置</h3>
        <div class="control-group">
          <el-button @click="exportChart" type="warning" size="small">导出图表</el-button>
        </div>
      </div>
      
      <div class="panel-section">
        <h3>专业筛选</h3>
        <div class="control-group">
          <el-select 
            v-model="selectedCategory" 
            @change="onCategoryChange" 
            placeholder="选择专业门类" 
            size="small"
          >
            <el-option 
              label="全部" 
              :value="null"
            ></el-option>
            <el-option 
              v-for="category in majorCategories" 
              :key="category.major_id"
              :label="category.major_name" 
              :value="category.major_id"
            ></el-option>
          </el-select>
          <el-select 
            v-model="selectedSubcategory" 
            @change="onSubcategoryChange" 
            placeholder="选择专业类" 
            size="small"
            :disabled="!selectedCategory"
          >
            <el-option 
              label="全部" 
              :value="null"
            ></el-option>
            <el-option 
              v-for="subcategory in availableSubcategories" 
              :key="subcategory.major_id"
              :label="subcategory.major_name" 
              :value="subcategory.major_id"
            ></el-option>
          </el-select>
        </div>
      </div>
      
      <div class="panel-section">
        <h3>专业搜索</h3>
        <div class="control-group">
          <el-input
            v-model="searchKeyword"
            @input="onSearchChange"
            placeholder="输入专业名称进行搜索"
            size="small"
            clearable
            style="width: 300px;"
          >
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-input>
          <el-button 
            @click="clearSearch" 
            type="info" 
            size="small" 
            plain
            :disabled="!searchKeyword"
          >
            清除搜索
          </el-button>
        </div>
        <div v-if="searchResults.length > 0" class="search-results">
          <span class="search-info">找到 {{ searchResults.length }} 个相关专业</span>
          <div v-if="searchResults.length > 1" class="search-list">
            <el-tag 
              v-for="subspecialty in searchResults" 
              :key="subspecialty.major_id"
              size="small"
              @click="selectSearchResult(subspecialty)"
              style="cursor: pointer; margin: 2px;"
            >
              {{ subspecialty.sub_name }}
            </el-tag>
          </div>
        </div>
      </div>
      
      <div class="panel-section" v-if="dataStatistics">
        <h3>数据统计</h3>
        <div class="statistics-info">
          <span class="stat-item">专业门类: {{ dataStatistics.totalCategories }}</span>
          <span class="stat-item">专业类: {{ dataStatistics.totalMajors }}</span>
          <span class="stat-item">具体专业: {{ dataStatistics.totalSubspecialties }}</span>
          <span class="stat-item">有分值专业: {{ dataStatistics.subspecialtiesWithScores }}</span>
        </div>
      </div>
      
    </div>

    <div class="chart-container">
      <div ref="sankeyChart" class="chart-wrapper"></div>
      <div class="help-icon" title="1. 长按左键拖拽平移&#10;2. Ctrl+滚轮控制缩放">
        <i class="el-icon-question"></i>
      </div>
    </div>


        </div>
</template>

<script>
import * as echarts from 'echarts'
import sankeyDataService from '@/utils/sankeyDataService'
// 开发环境下导入测试工具
if (process.env.NODE_ENV === 'development') {
  import('@/utils/testSankeyApi').then(module => {
    window.testSankeyAPI = module.testSankeyAPI
    window.testScoreField = module.testScoreField
  })
}

export default {
  name: 'SankeyView',
  data() {
    return {
      sankeyChart: null,
      chartTheme: 'light',
      chartOrientation: 'horizontal',
      loading: false,
      // 专业数据相关
      majorCategories: [], // 专业门类列表
      rawMajorData: [], // 原始专业数据
      rawSubspecialtyData: [], // 原始子专业数据
      selectedCategory: null, // 选中的专业门类
      selectedSubcategory: null, // 选中的专业类（子专业）
      availableSubcategories: [], // 可用的专业类列表
      dataStatistics: null, // 数据统计信息
      baseSankeyData: null, // 基础桑基图数据（不含扩展列）
      sankeyData: {
        nodes: [],
        links: []
      },
      chartContainer: null, // 图表容器引用
      // 搜索相关
      searchKeyword: '', // 搜索关键词
      searchResults: [], // 搜索结果
      originalSankeyData: null, // 保存原始数据用于搜索恢复
      highlightedNodeName: null, // 当前高亮的节点名称
      // 平移和缩放相关
      isPanning: false, // 是否正在平移
      panStartX: 0, // 平移开始的X坐标
      panStartY: 0, // 平移开始的Y坐标
      panStartScrollLeft: 0, // 平移开始时的滚动位置
      panStartScrollTop: 0, // 平移开始时的滚动位置
      zoomLevel: 1, // 当前缩放级别
      minZoom: 0.5, // 最小缩放级别
      maxZoom: 3, // 最大缩放级别
      chartWrapper: null // 图表包装器引用
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initSankeyChart()
      this.initializeData()
      this.initChartContainerScroll()
      this.initPanAndZoom()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.chartContainer) {
      this.chartContainer.removeEventListener('wheel', this.handleWheelScroll)
      this.chartContainer.removeEventListener('wheel', this.handleZoom)
      this.chartContainer.removeEventListener('mousedown', this.handleMouseDown)
      this.chartContainer.removeEventListener('mousemove', this.handleMouseMove)
      this.chartContainer.removeEventListener('mouseup', this.handleMouseUp)
      this.chartContainer.removeEventListener('mouseleave', this.handleMouseLeave)
    }
    if (this.sankeyChart) {
      this.sankeyChart.dispose()
    }
  },
  methods: {
    /**
     * 初始化图表容器滚动功能
     */
    initChartContainerScroll() {
      // 获取图表容器的父容器
      if (!this.chartContainer) {
        this.chartContainer = this.$el.querySelector('.chart-container')
      }
      if (this.chartContainer) {
        this.chartContainer.addEventListener('wheel', this.handleWheelScroll, { passive: false })
      }
    },

    /**
     * 处理鼠标滚轮事件，实现左右滑动
     */
    handleWheelScroll(event) {
      // 如果按住了中键或者Ctrl键，则进行缩放而不是滚动
      if (event.button === 1 || event.ctrlKey) {
        return
      }
      
      // 阻止默认的垂直滚动
      event.preventDefault()
      
      // 使用 deltaY 来控制水平滚动
      // deltaY > 0 表示向下滚动，我们让它向右滚动
      // deltaY < 0 表示向上滚动，我们让它向左滚动
      const scrollAmount = event.deltaY
      
      // 应用水平滚动
      this.chartContainer.scrollLeft += scrollAmount
    },

    /**
     * 初始化平移和缩放功能
     */
    initPanAndZoom() {
      if (!this.chartContainer) {
        this.chartContainer = this.$el.querySelector('.chart-container')
      }
      if (!this.chartWrapper) {
        this.chartWrapper = this.$el.querySelector('.chart-wrapper')
      }
      
      if (this.chartContainer && this.chartWrapper) {
        // 添加鼠标事件监听器
        this.chartContainer.addEventListener('mousedown', this.handleMouseDown, { passive: false })
        this.chartContainer.addEventListener('mousemove', this.handleMouseMove, { passive: false })
        this.chartContainer.addEventListener('mouseup', this.handleMouseUp, { passive: false })
        this.chartContainer.addEventListener('mouseleave', this.handleMouseLeave, { passive: false })
        this.chartContainer.addEventListener('wheel', this.handleZoom, { passive: false })
        
        // 禁用右键菜单
        this.chartContainer.addEventListener('contextmenu', (e) => e.preventDefault())
        
        // 设置初始样式
        this.chartWrapper.style.transformOrigin = '0 0'
        this.chartWrapper.style.transform = `scale(${this.zoomLevel})`
      }
    },

    /**
     * 处理鼠标按下事件
     */
    handleMouseDown(event) {
      // 只处理左键按下
      if (event.button === 0) {
        this.isPanning = true
        this.panStartX = event.clientX
        this.panStartY = event.clientY
        this.panStartScrollLeft = this.chartContainer.scrollLeft
        this.panStartScrollTop = this.chartContainer.scrollTop
        
        // 改变光标样式
        this.chartContainer.style.cursor = 'grabbing'
        
        // 阻止默认行为
        event.preventDefault()
      }
    },

    /**
     * 处理鼠标移动事件
     */
    handleMouseMove(event) {
      if (!this.isPanning) return
      
      event.preventDefault()
      
      // 计算移动距离
      const deltaX = event.clientX - this.panStartX
      const deltaY = event.clientY - this.panStartY
      
      // 应用平移
      this.chartContainer.scrollLeft = this.panStartScrollLeft - deltaX
      this.chartContainer.scrollTop = this.panStartScrollTop - deltaY
    },

    /**
     * 处理鼠标抬起事件
     */
    handleMouseUp(event) {
      if (event.button === 0) {
        this.isPanning = false
        this.chartContainer.style.cursor = 'grab'
      }
    },

    /**
     * 处理鼠标离开事件
     */
    handleMouseLeave(event) {
      this.isPanning = false
      this.chartContainer.style.cursor = 'default'
    },

    /**
     * 处理缩放事件
     */
    handleZoom(event) {
      // 检查是否按住Ctrl键进行缩放
      if (event.ctrlKey) {
        event.preventDefault()
        
        // 计算缩放因子
        const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1
        const newZoomLevel = this.zoomLevel * zoomFactor
        
        // 限制缩放范围
        if (newZoomLevel >= this.minZoom && newZoomLevel <= this.maxZoom) {
          this.zoomLevel = newZoomLevel
          
          // 应用缩放
          this.chartWrapper.style.transform = `scale(${this.zoomLevel})`
          
          // 重新调整ECharts图表大小
          if (this.sankeyChart) {
            this.$nextTick(() => {
              this.sankeyChart.resize()
            })
          }
          
        }
      }
    },


    initSankeyChart() {
      if (!this.$refs.sankeyChart) return
      
      this.sankeyChart = echarts.init(this.$refs.sankeyChart)
      this.updateChart()
    },
    
    updateChart() {
      if (!this.sankeyChart) return
      
      // 调试日志
      console.log('更新图表，节点数:', this.sankeyData.nodes?.length, '连接数:', this.sankeyData.links?.length)
      
      const option = {
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          formatter: function(params) {
            if (params.dataType === 'edge') {
              return `${params.data.source} → ${params.data.target}<br/>数值: ${Math.round(params.data.value)}`
            } else {
              return `${params.name}<br/>总量: ${Math.round(params.value)}`
            }
          }
        },
        backgroundColor: this.chartTheme === 'dark' ? '#1e1e1e' : '#fff',
        series: [
          {
            type: 'sankey',
            data: this.sankeyData.nodes,
            links: this.sankeyData.links,
            emphasis: {
              focus: 'adjacency'
            },
            orient: this.chartOrientation === 'horizontal' ? 'horizontal' : 'vertical',
            nodeAlign: 'justify', // 使每列高度对齐
            layoutIterations: 32, // 布局迭代次数
            nodeGap: 1, // 进一步减小节点间距
            nodeWidth: 15, // 减小节点宽度
            nodePadding: 4, // 减小节点内边距
            left: '3%',
            right: '3%', // 减小右侧留白，让列更密集
            top: '5%',
            bottom: '3%',
            lineStyle: {
              color: 'gradient',
              curveness: 0.5,
              opacity: 0.3 // 降低连线透明度，让标签更清晰
            },
            itemStyle: {
              borderWidth: 1,
              borderColor: this.chartTheme === 'dark' ? '#555' : '#aaa'
            },
            label: {
              show: true,
              position: 'right', // 标签显示在节点右侧
              color: this.chartTheme === 'dark' ? '#fff' : '#333',
              fontSize: 7, // 减小字体
              formatter: function(params) {
                // 对于长名称进行截断
                const name = params.name
                const maxLength = 10
                if (name.length > maxLength) {
                  return name.substring(0, maxLength) + '...'
                }
                return name
              },
              padding: [1, 3], // 减小内边距
              backgroundColor: 'rgba(255, 255, 255, 0.7)', // 添加背景色避免重叠
              borderRadius: 2
            }
          }
        ]
      }
      
      // 调整图表容器尺寸以适应节点数量和方向
      if (this.$refs.sankeyChart) {
        const nodeCount = this.sankeyData.nodes?.length || 0
        
        if (this.chartOrientation === 'horizontal') {
          // 水平方向：减小整体尺寸
          const minHeight = Math.max(1500, nodeCount * 4) // 每个节点4px高度，整体缩小
          const minWidth = 1800 // 进一步减小宽度，让列更紧密
          this.$refs.sankeyChart.style.height = `${minHeight}px`
          this.$refs.sankeyChart.style.width = `${minWidth}px`
        } else {
          // 垂直方向：主要减小高度
          const minHeight = Math.max(1500, nodeCount * 4)
          this.$refs.sankeyChart.style.height = `${minHeight}px`
          this.$refs.sankeyChart.style.width = '100%'
        }
        
        this.sankeyChart.resize()
      }
      
      this.sankeyChart.setOption(option, true)
      
      // 如果有需要高亮的节点，在图表更新后定位
      if (this.highlightedNodeName) {
        this.$nextTick(() => {
          this.highlightAndScrollToNode(this.highlightedNodeName)
        })
      }
    },
    
    handleResize() {
      if (this.sankeyChart) {
        this.sankeyChart.resize()
      }
    },
    
    loadSampleData() {
      // 加载另一组示例数据
      this.sankeyData = {
        nodes: [
          { name: '访问来源' },
          { name: '搜索引擎' },
          { name: '直接访问' },
          { name: '社交媒体' },
          { name: '首页' },
          { name: '产品页' },
          { name: '关于我们' },
          { name: '购买' },
          { name: '注册' },
          { name: '离开' }
        ],
        links: [
          { source: '搜索引擎', target: '首页', value: 300 },
          { source: '直接访问', target: '首页', value: 200 },
          { source: '社交媒体', target: '首页', value: 100 },
          { source: '首页', target: '产品页', value: 250 },
          { source: '首页', target: '关于我们', value: 150 },
          { source: '首页', target: '离开', value: 200 },
          { source: '产品页', target: '购买', value: 100 },
          { source: '产品页', target: '注册', value: 80 },
          { source: '产品页', target: '离开', value: 70 },
          { source: '关于我们', target: '注册', value: 50 },
          { source: '关于我们', target: '离开', value: 100 }
        ]
      }
      this.updateChart()
      this.updateTableData()
      this.$message.success('示例数据已加载')
    },
    
    
    refreshChart() {
      this.updateChart()
      this.$message.success('图表已刷新')
    },
    
    exportChart() {
      if (this.sankeyChart) {
        const url = this.sankeyChart.getDataURL({
          type: 'png',
          backgroundColor: this.chartTheme === 'dark' ? '#1e1e1e' : '#fff'
        })
        
        const link = document.createElement('a')
        link.download = 'sankey-chart.png'
        link.href = url
        link.click()
        
        this.$message.success('图表已导出')
          }
    },
    
    
    goBack() {
      this.$router.push('/radar')
    },

    /**
     * 搜索功能相关方法
     */
    onSearchChange() {
      if (!this.searchKeyword.trim()) {
        this.clearSearch()
        return
      }
      
      this.performSearch()
    },

    async performSearch() {
      const keyword = this.searchKeyword.trim().toLowerCase()
      if (!keyword) return

      // 在具体专业中搜索匹配的项目
      const matchedSubspecialties = this.rawSubspecialtyData.filter(subspecialty => 
        subspecialty.sub_name && subspecialty.sub_name.toLowerCase().includes(keyword)
      )

      this.searchResults = matchedSubspecialties

      if (matchedSubspecialties.length > 0) {
        // 获取所有匹配的专业ID
        const subspecialtyIds = matchedSubspecialties.map(s => s.major_id)
        
        // 根据匹配的具体专业筛选数据
        this.baseSankeyData = sankeyDataService.filterBySubspecialtyIds(
          this.rawMajorData, 
          this.rawSubspecialtyData, 
          subspecialtyIds
        )
        
        // 应用扩展列
        await this.applyExtendedColumns()
        
        // 收集所有匹配专业所属的专业门类
        const matchedCategories = new Set()
        const matchedSubcategories = new Set()
        
        matchedSubspecialties.forEach(subspecialty => {
          const category = this.majorCategories.find(c => 
            c.children && c.children.some(child => child.major_id === subspecialty.parent_id)
          )
          if (category) {
            matchedCategories.add(category.major_id)
            matchedSubcategories.add(subspecialty.parent_id)
          }
        })
        
        // 如果只有一个专业门类，自动选择它
        if (matchedCategories.size === 1) {
          const categoryId = Array.from(matchedCategories)[0]
          const category = this.majorCategories.find(c => c.major_id === categoryId)
          if (category) {
            this.selectedCategory = categoryId
            this.availableSubcategories = category.children || []
            
            // 如果只有一个专业类，也自动选择它
            if (matchedSubcategories.size === 1) {
              const subcategoryId = Array.from(matchedSubcategories)[0]
              this.selectedSubcategory = subcategoryId
            } else {
              this.selectedSubcategory = null
            }
          }
        } else {
          // 多个专业门类时，不自动选择
          this.selectedCategory = null
          this.selectedSubcategory = null
          this.availableSubcategories = []
        }
        
        // 如果只找到一个匹配的专业，定位到该专业
        if (matchedSubspecialties.length === 1) {
          const subspecialtyName = matchedSubspecialties[0].sub_name.trim()
          this.highlightedNodeName = subspecialtyName
          this.$nextTick(() => {
            this.highlightAndScrollToNode(subspecialtyName)
          })
          this.$message.success(`已找到并显示专业：${matchedSubspecialties[0].sub_name}`)
        } else {
          // 多个匹配时，定位到第一个
          const firstSubspecialtyName = matchedSubspecialties[0].sub_name.trim()
          this.highlightedNodeName = firstSubspecialtyName
          this.$nextTick(() => {
            this.highlightAndScrollToNode(firstSubspecialtyName)
          })
          const subspecialtyNames = matchedSubspecialties.slice(0, 5).map(s => s.sub_name).join('、')
          const moreText = matchedSubspecialties.length > 5 ? `等${matchedSubspecialties.length}个` : ''
          this.$message.info(`已找到并显示 ${matchedSubspecialties.length} 个相关专业：${subspecialtyNames}${moreText}`)
        }
      } else {
        this.highlightedNodeName = null
        this.$message.warning('未找到相关专业')
        this.searchResults = []
      }
    },

    clearSearch() {
      this.searchKeyword = ''
      this.searchResults = []
      this.highlightedNodeName = null
      
      // 取消高亮
      if (this.sankeyChart) {
        this.sankeyChart.dispatchAction({
          type: 'downplay'
        })
      }
      
      // 重置选择状态
      this.selectedCategory = null
      this.selectedSubcategory = null
      this.availableSubcategories = []
      
      // 显示所有数据
      this.baseSankeyData = sankeyDataService.convertToSankeyData(
        this.rawMajorData, 
        this.rawSubspecialtyData
      )
      
      // 应用扩展列
      this.applyExtendedColumns()
      
      this.$message.success('已清除搜索，显示所有专业')
    },

    async selectSearchResult(subspecialty) {
      const subspecialtyIds = [subspecialty.major_id]
      this.searchKeyword = subspecialty.sub_name
      
      // 根据选中的具体专业筛选数据
      this.baseSankeyData = sankeyDataService.filterBySubspecialtyIds(
        this.rawMajorData, 
        this.rawSubspecialtyData, 
        subspecialtyIds
      )
      
      // 应用扩展列
      await this.applyExtendedColumns()
      
      // 自动选择对应的专业门类和专业类
      const category = this.majorCategories.find(c => 
        c.children && c.children.some(child => child.major_id === subspecialty.parent_id)
      )
      if (category) {
        this.selectedCategory = category.major_id
        this.availableSubcategories = category.children || []
        const subcategory = category.children.find(child => child.major_id === subspecialty.parent_id)
        if (subcategory) {
          this.selectedSubcategory = subcategory.major_id
        }
      }
      
      // 定位到选中的专业
      const subspecialtyName = subspecialty.sub_name.trim()
      this.highlightedNodeName = subspecialtyName
      this.$nextTick(() => {
        this.highlightAndScrollToNode(subspecialtyName)
      })
      
      this.$message.success(`已选择专业：${subspecialty.sub_name}`)
    },

    /**
     * 高亮并滚动到指定节点
     * 以专业列（最后一列）为基准，将该列居中显示在屏幕中间
     * @param {string} nodeName - 节点名称
     */
    highlightAndScrollToNode(nodeName) {
      if (!this.sankeyChart || !nodeName) return
      
      try {
        // 先取消之前的高亮
        this.sankeyChart.dispatchAction({
          type: 'downplay'
        })
        
        // 等待图表渲染完成
        setTimeout(() => {
          // 高亮指定节点
          this.sankeyChart.dispatchAction({
            type: 'highlight',
            name: nodeName
          })
          
          // 获取图表容器的位置信息
          const chartElement = this.$refs.sankeyChart
          if (!chartElement || !this.chartContainer) return
          
          // 获取图表实例，查找节点位置
          const chartInstance = this.sankeyChart
          const option = chartInstance.getOption()
          
          // 查找节点在数据中的索引
          let nodeIndex = -1
          if (option && option.series && option.series[0] && option.series[0].data) {
            nodeIndex = option.series[0].data.findIndex(node => node.name === nodeName)
          }
          
          if (nodeIndex === -1) {
            console.warn('未找到节点:', nodeName)
            return
          }
          
          const chartRect = chartElement.getBoundingClientRect()
          const containerRect = this.chartContainer.getBoundingClientRect()
          const chartWidth = chartElement.offsetWidth
          const chartHeight = chartElement.offsetHeight
          
          // 桑基图是水平方向的，节点按列排列
          // 列的顺序：专业门类 -> 专业类 -> 具体专业（最后一列）
          // 我们需要定位到最后一列（专业列）的水平中心位置
          
          // 分析节点数据，找出哪些节点是最后一列（专业列）的节点
          // 在桑基图中，最后一列的节点只作为target出现，不会作为source
          const links = option.series[0].links || []
          const sourceNodes = new Set(links.map(link => link.source))
          const targetNodes = new Set(links.map(link => link.target))
          
          // 找出只作为target的节点（这些是最后一列的节点）
          const lastColumnNodes = Array.from(targetNodes).filter(node => !sourceNodes.has(node))
          
          // 判断当前节点是否在最后一列
          const isLastColumn = lastColumnNodes.includes(nodeName)
          
          // 获取图表配置中的左右边距
          const paddingLeft = chartWidth * 0.03 // 对应配置中的left: '3%'
          const paddingRight = chartWidth * 0.03 // 对应配置中的right: '3%'
          const availableWidth = chartWidth - paddingLeft - paddingRight
          
          // 计算最后一列（专业列）的中心位置
          // 在水平桑基图中，如果有3列，最后一列大约在可用宽度的 85-90% 处
          // 如果有扩展列，最后一列会更靠右
          // 根据实际布局，专业列通常在图表右侧，大约在 85-90% 的位置
          const lastColumnCenterX = paddingLeft + availableWidth * 0.875 // 最后一列的中心位置
          
          // 计算该列在页面中的绝对位置（考虑缩放）
          const columnPageX = chartRect.left + lastColumnCenterX * this.zoomLevel
          
          // 计算列相对于容器左边的距离
          const columnRelativeToContainer = columnPageX - containerRect.left
          
          // 计算需要水平滚动的距离，使专业列居中显示
          // 当前滚动位置 + 列相对位置 - 容器宽度的一半
          const targetScrollLeft = this.chartContainer.scrollLeft + columnRelativeToContainer - (containerRect.width / 2)
          
          // 同时垂直滚动到节点位置（使节点在垂直方向也可见）
          const totalNodes = option.series[0].data.length
          const paddingTop = chartHeight * 0.05
          const paddingBottom = chartHeight * 0.03
          const availableHeight = chartHeight - paddingTop - paddingBottom
          const estimatedNodeYInChart = paddingTop + (availableHeight / totalNodes) * (nodeIndex + 0.5)
          const nodePageY = chartRect.top + estimatedNodeYInChart * this.zoomLevel
          const nodeRelativeToContainer = nodePageY - containerRect.top
          const targetScrollTop = this.chartContainer.scrollTop + nodeRelativeToContainer - (containerRect.height / 2)
          
          // 平滑滚动，水平居中专业列，垂直居中节点
          this.chartContainer.scrollTo({
            top: Math.max(0, targetScrollTop),
            left: Math.max(0, targetScrollLeft),
            behavior: 'smooth'
          })
        }, 500) // 等待图表渲染完成，增加延迟确保布局完成
      } catch (error) {
        console.error('定位节点失败:', error)
      }
    },

    /**
     * 初始化数据
     */
    async initializeData() {
      // 自动加载专业数据
      await this.loadMajorData()
      
      // 设置默认选择：第一个专业门类和第一个专业类
      if (this.majorCategories.length > 0) {
        this.selectedCategory = this.majorCategories[0].major_id
        
        // 更新可用的子专业列表
        const firstCategory = this.majorCategories[0]
        this.availableSubcategories = firstCategory?.children || []
        
        // 如果有子专业，选择第一个
        if (this.availableSubcategories.length > 0) {
          this.selectedSubcategory = this.availableSubcategories[0].major_id
          
          // 根据默认选择筛选数据
          this.baseSankeyData = sankeyDataService.filterBySubcategoryId(
            this.rawMajorData, 
            this.rawSubspecialtyData, 
            this.selectedSubcategory
          )
        } else {
          // 如果没有子专业，只按门类筛选
          this.baseSankeyData = sankeyDataService.filterByCategoryId(
            this.rawMajorData, 
            this.rawSubspecialtyData, 
            this.selectedCategory
          )
        }
        
        // 应用扩展列
        await this.applyExtendedColumns()
        
        const categoryName = firstCategory.major_name
        const subcategoryName = this.selectedSubcategory ? 
          this.availableSubcategories[0]?.major_name : ''
        
        this.$message.success(`已默认选择：${categoryName}${subcategoryName ? ' - ' + subcategoryName : ''}`)
      }
    },

    /**
     * 加载专业数据
     */
    async loadMajorData() {
      this.loading = true
      try {
        const result = await sankeyDataService.getSankeyData()
        
        if (result.success) {
          // 保存原始数据
          this.rawMajorData = result.rawData.majorList
          this.rawSubspecialtyData = result.rawData.subspecialtyList
          this.majorCategories = result.rawData.majorList
          
          // 更新统计信息
          this.dataStatistics = sankeyDataService.getStatistics(
            this.rawMajorData, 
            this.rawSubspecialtyData
          )
          
          console.log(`成功加载专业数据，共 ${this.dataStatistics.totalSubspecialties} 个专业`)
        } else {
          this.$message.error('加载专业数据失败：' + result.error)
        }
      } catch (error) {
        console.error('加载专业数据失败:', error)
        this.$message.error('加载专业数据失败：' + error.message)
      } finally {
        this.loading = false
      }
    },

    /**
     * 专业门类变化处理
     */
    async onCategoryChange(categoryId) {
      // 重置子专业选择
      this.selectedSubcategory = null
      
      if (categoryId === null || categoryId === undefined) {
        // 选择"全部"，显示所有数据
        this.availableSubcategories = []
        this.baseSankeyData = sankeyDataService.convertToSankeyData(
          this.rawMajorData, 
          this.rawSubspecialtyData
        )
      } else {
        // 更新可用的子专业列表
        const selectedCategory = this.majorCategories.find(c => c.major_id === categoryId)
        this.availableSubcategories = selectedCategory?.children || []
        
        // 根据选中的专业门类筛选数据
        this.baseSankeyData = sankeyDataService.filterByCategoryId(
          this.rawMajorData, 
          this.rawSubspecialtyData, 
          categoryId
        )
      }
      
      // 应用扩展列
      await this.applyExtendedColumns()
      
      const categoryName = categoryId ? 
        this.majorCategories.find(c => c.major_id === categoryId)?.major_name : '全部'
      this.$message.success(`已切换到：${categoryName}`)
    },

    /**
     * 专业类变化处理
     */
    async onSubcategoryChange(subcategoryId) {
      if (this.selectedCategory === null || this.selectedCategory === undefined) {
        this.$message.warning('请先选择专业门类')
        return
      }
      
      if (subcategoryId === null || subcategoryId === undefined) {
        // 选择"全部"，显示整个门类的数据
        this.baseSankeyData = sankeyDataService.filterByCategoryId(
          this.rawMajorData, 
          this.rawSubspecialtyData, 
          this.selectedCategory
        )
      } else {
        // 根据选中的专业类筛选数据
        this.baseSankeyData = sankeyDataService.filterBySubcategoryId(
          this.rawMajorData, 
          this.rawSubspecialtyData, 
          subcategoryId
        )
      }
      
      // 应用扩展列
      await this.applyExtendedColumns()
      
      const subcategoryName = subcategoryId ? 
        this.availableSubcategories.find(c => c.major_id === subcategoryId)?.major_name : '全部'
      this.$message.success(`已切换到专业类：${subcategoryName}`)
    },


    /**
     * 应用扩展列
     */
    async applyExtendedColumns() {
      if (!this.baseSankeyData || !this.rawSubspecialtyData) {
        console.error('缺少基础数据:', {
          hasBaseSankeyData: !!this.baseSankeyData,
          hasRawSubspecialtyData: !!this.rawSubspecialtyData
        })
        return
      }
      
      try {
        console.log('开始应用扩展列...')
        console.log('基础数据:', this.baseSankeyData)
        console.log('子专业数量:', this.rawSubspecialtyData.length)
        
        // 生成扩展数据
        this.sankeyData = await sankeyDataService.extendSankeyDataWithAttributes(
          this.baseSankeyData,
          this.rawSubspecialtyData
        )
        
        console.log('扩展后数据:', this.sankeyData)
        console.log('扩展后节点数:', this.sankeyData.nodes.length)
        console.log('扩展后连接数:', this.sankeyData.links.length)
        
        // 保存原始数据用于搜索功能
        this.originalSankeyData = JSON.parse(JSON.stringify(this.sankeyData))
        
        this.updateChart()
      } catch (error) {
        console.error('应用扩展列失败:', error)
        throw error
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.sankey-page {
  width: 100vw;
  height: 100vh;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.control-panel {
  display: flex;
  gap: 2rem;
  padding: 1rem 2rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.panel-section {
  h3 {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 0.5rem 0;
  }
}

.control-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.chart-container {
  flex: 1;
  padding: 1rem 2rem;
  overflow: auto; /* 允许滚动 */
  min-height: 0; /* 确保 flex 子项可以滚动 */
  cursor: grab; /* 默认抓取光标 */
  user-select: none; /* 禁止文本选择 */
  position: relative; /* 为问号图标定位 */
  
  &:active {
    cursor: grabbing; /* 拖拽时的光标 */
  }
}

.chart-wrapper {
  width: 100%;
  min-height: 2000px; /* 减小最小高度 */
  height: auto; /* 自动高度 */
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transform-origin: 0 0; /* 设置缩放原点 */
  transition: transform 0.1s ease-out; /* 平滑缩放动画 */
}

.help-icon {
  position: absolute;
  top: 2rem;
  right: 3rem;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  z-index: 1000;
  
  &:hover {
    background: #409eff;
    border-color: #409eff;
    transform: scale(1.1);
    
    i {
      color: white;
    }
  }
  
  i {
    font-size: 16px;
    color: #606266;
    transition: color 0.2s ease;
  }
}

.data-editor {
  .editor-tips {
    background: #f3f4f6;
    padding: 1rem;
    border-radius: 6px;
    margin-bottom: 1rem;
    
    p {
      margin: 0.25rem 0;
      font-size: 0.875rem;
      color: #374151;
      
      &:first-child {
        font-weight: 600;
        margin-bottom: 0.5rem;
      }
    }
  }
}

.table-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.el-table {
  .el-input {
    border: none;
    
    .el-input__inner {
      border: none;
      padding: 5px 8px;
      
      &:focus {
        border: 1px solid #409EFF;
      }
    }
  }
  
  .el-input-number {
    .el-input__inner {
      border: none;
      padding: 5px 8px;
      
      &:focus {
        border: 1px solid #409EFF;
      }
    }
  }
}

.dialog-footer {
  text-align: right;
}

.statistics-info {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  
  .stat-item {
    background: #f3f4f6;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    color: #374151;
    white-space: nowrap;
  }
}


.search-results {
  margin-top: 0.5rem;
  
  .search-info {
    font-size: 0.75rem;
    color: #10b981;
    background: #ecfdf5;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    border: 1px solid #d1fae5;
    display: block;
    margin-bottom: 0.5rem;
  }
  
  .search-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    
    .el-tag {
      transition: all 0.2s;
      
      &:hover {
        background-color: #409eff;
        color: white;
        transform: translateY(-1px);
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .control-panel {
    flex-direction: column;
    gap: 1rem;
  }
  
  .sankey-page {
    padding: 0;
  }
  
  .chart-container {
    padding: 1rem;
  }
}
</style>
